/**
 * Mic button for a textarea.
 *
 * Thin on purpose. Everything decidable lives in tested modules — quota rules
 * in ./quota, text placement in ./transcript, outcome wording in ./feedback,
 * and the session's choices (which engine, clip lengths, what a failed clip
 * does) in ./session — and what remains here is browser plumbing that can only
 * be exercised with a real microphone.
 *
 * ─── CLIPS ──────────────────────────────────────────────────────────────────
 * Each clip is its OWN MediaRecorder recording: stopped after its length,
 * uploaded, and replaced by a fresh recorder on the same stream. Until
 * 2026-09-19 one recorder was sliced with `timeslice`, which gives only the
 * FIRST slice a container header — every later slice was a headerless
 * fragment a transcriber can't read. Separate recordings are each a complete
 * file, at the cost of a gap of a few milliseconds between clips.
 *
 * The first clip is ~5 s (session.ts: FIRST_CLIP_MS), so words appear — or a
 * problem shows — while the person is still talking, instead of when they
 * press stop.
 */

import {
  transcribeClip, fetchVoiceQuota, checkTranscribeService, TranscriptionFailedError,
} from '@/lib/data/voice';
import { feedbackForSuccess, feedbackForError } from '@/lib/voice/feedback';
import { webSpeechAvailable, startLocalDictation, type LocalDictation }
  from '@/lib/voice/web-speech';
import { canDictate, quotaMessage, type QuotaStatus } from '@/lib/voice/quota';
import { insertDictation } from '@/lib/voice/transcript';
import {
  applySpokenFormat, continueNumbering, hasSpokenFormat, listeningHint, INITIAL_FORMAT_STATE,
  type FormatState,
} from '@/lib/voice/dictation-format';
import { tidyDictation, type TidyKind } from '@/lib/data/voice';
import {
  clipLengthMs, startPlan, afterClipFailure, afterLocalFailure, keptClipsMessage, levelFromSamples, barsLit,
  isSilentTooLong, formatElapsed, PREFLIGHT_TIMEOUT_MS, RETRY_DELAY_MS, MAX_AUTO_RETRIES,
  QUIET_POLL_MS, QUIET_ATTEMPTS,
} from '@/lib/voice/session';

/** Cached so every mic on a page doesn't re-ask. Refreshed after each clip. */
let cachedQuota: QuotaStatus | null = null;
let quotaLoaded = false;

export async function primeVoiceQuota(): Promise<QuotaStatus | null> {
  cachedQuota = await fetchVoiceQuota();
  quotaLoaded = true;
  return cachedQuota;
}

/** True when dictation is worth offering at all. */
export function voiceAvailable(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';
}

interface Clip {
  blob: Blob;
  seconds: number;
  /** Failed attempts so far — bounded by MAX_AUTO_RETRIES. */
  attempts: number;
}

/**
 * Attach a mic button to a textarea. No-op when unsupported, so callers can
 * add it unconditionally.
 */
export function attachVoiceInput(textarea: HTMLTextAreaElement): () => void {
  const noop = (): void => {};
  if (!voiceAvailable()) return noop;
  if (textarea.dataset.voiceAttached === '1') return noop;
  textarea.dataset.voiceAttached = '1';

  const field = textarea.closest('.field') ?? textarea.parentElement;
  if (!field) return noop;
  (field as HTMLElement).classList.add('has-voice');

  const btn = document.createElement('button');
  btn.type = 'button';           // inside a <form>; the default would submit it
  btn.className = 'voice-btn';
  btn.title = 'Dictate';
  btn.setAttribute('aria-label', 'Dictate');
  btn.innerHTML = micIcon();
  field.appendChild(btn);

  // Live "I can hear you" bars and a timer, shown only while recording.
  const meter = document.createElement('div');
  meter.className = 'voice-meter';
  meter.hidden = true;
  meter.setAttribute('aria-hidden', 'true');
  meter.innerHTML = `<span class="voice-bars">${'<i></i>'.repeat(5)}</span><span class="voice-time">0:00</span>`;
  field.appendChild(meter);
  const barEls = [...meter.querySelectorAll('i')];
  const timeEl = meter.querySelector<HTMLElement>('.voice-time')!;

  const hint = document.createElement('div');
  hint.className = 'voice-hint';
  hint.hidden = true;
  field.appendChild(hint);

  const retryBtn = document.createElement('button');
  retryBtn.type = 'button';
  retryBtn.className = 'btn-link voice-retry';
  retryBtn.textContent = 'Retry';
  retryBtn.hidden = true;
  field.appendChild(retryBtn);

  // The tidy pass rewrites a field the designer just watched fill itself in,
  // so it must always be one tap from undone. Shown only while there is
  // something to undo.
  const undoBtn = document.createElement('button');
  undoBtn.type = 'button';
  undoBtn.className = 'btn-link voice-undo';
  undoBtn.textContent = 'Undo formatting';
  undoBtn.hidden = true;
  field.appendChild(undoBtn);

  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let clipTimer: ReturnType<typeof setTimeout> | null = null;
  let clipIndex = 0;
  /** True while clips are being recorded for the server. */
  let serverActive = false;
  /** The person pressed stop. Nothing may start listening again after this. */
  let userStopped = false;
  /** A start is in progress (permission prompt, readiness check). */
  let starting = false;
  /** Set once we've switched to the browser's own dictation this session. */
  let local: LocalDictation | null = null;
  /** What to do if that dictation can't run — from the session's start plan. */
  let onLocalFailure: 'queue' | 'none' = 'none';
  /** Clips that failed and wait for the Retry button. Never silently dropped. */
  const kept: Clip[] = [];
  const retryTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Timers for the post-dictation tidy wait. Kept OUT of retryTimers — see whenQuiet. */
  const quietTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Clips being uploaded right now. */
  let inflight = 0;

  /**
   * Numbering carried across clips (0048's dictation formatting). Clips are
   * separate recordings, so a list spanning one would otherwise restart at 1.
   */
  let formatState: FormatState = { ...INITIAL_FORMAT_STATE };
  /** True once the designer has spoken any structure command this session. */
  let spokeStructure = false;
  /** The field exactly as it stood before the tidy pass rewrote it. */
  let beforeTidy: string | null = null;

  /**
   * What sort of field this is, for the tidy pass. Read off the textarea so a
   * caller sets it declaratively (`voiceKind` on the modal field) and nothing
   * here needs to know which screen it is on. 'notes' is the safe default: it
   * may make a list but never invents one.
   */
  const tidyKind = (): TidyKind => {
    const v = textarea.dataset.voiceKind;
    return v === 'line' || v === 'scope' ? v : 'notes';
  };

  /**
   * Mark the field while any dictation is still waiting to become text —
   * uploading, scheduled for a retry, or kept. The modal reads this
   * (`data-voice-pending`) to ask before a close or save would lose it.
   */
  const markPending = (): void => {
    const pending = inflight > 0 || retryTimers.size > 0 || kept.length > 0;
    if (pending) (field as HTMLElement).dataset.voicePending = '1';
    else delete (field as HTMLElement).dataset.voicePending;
  };

  // Level meter.
  let audioCtx: AudioContext | null = null;
  let rafId = 0;
  let recordingStartedAt = 0;
  let lastHeardAt = 0;
  let silenceWarned = false;

  /**
   * Put transcribed text at the caret, whichever engine produced it.
   *
   * Spoken commands are applied HERE rather than in the Edge Function, so they
   * work identically on the device's own dictation — which never reaches that
   * function. Formatting that only existed server-side would silently not
   * apply on a job site with no signal, which is the case the fallback is for.
   */
  const insertText = (text: string): void => {
    // More words arrived after a tidy pass — the snapshot no longer contains
    // everything that was said, so undoing to it would quietly drop them.
    if (beforeTidy !== null) { beforeTidy = null; undoBtn.hidden = true; }
    if (hasSpokenFormat(text)) spokeStructure = true;
    const formatted = applySpokenFormat(text, formatState);
    formatState = formatted.state;
    const { value, cursor } = insertDictation(
      textarea.value, formatted.text,
      { start: textarea.selectionStart, end: textarea.selectionEnd },
    );
    textarea.value = value;
    textarea.setSelectionRange(cursor, cursor);
    // Let anything listening for edits (autosave, validation) notice.
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const setHint = (msg: string | null, tone: 'info' | 'warn' = 'info'): void => {
    hint.textContent = msg ?? '';
    hint.hidden = !msg;
    hint.classList.toggle('voice-hint-warn', tone === 'warn');
  };

  /** Show the kept-clips prompt and its Retry button, or clear them. */
  const showKept = (): void => {
    const msg = keptClipsMessage(kept.length, navigator.onLine !== false);
    retryBtn.hidden = !msg;
    if (msg) setHint(msg, 'warn');
  };

  const setRecording = (on: boolean): void => {
    btn.classList.toggle('is-recording', on);
    btn.title = on ? 'Stop dictating' : 'Dictate';
    btn.setAttribute('aria-label', btn.title);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  };

  // ── Level meter ──

  /** Put the field back exactly as it was before the tidy pass. */
  const undoTidy = (): void => {
    if (beforeTidy === null) return;
    textarea.value = beforeTidy;
    beforeTidy = null;
    undoBtn.hidden = true;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    textarea.focus();
  };

  /**
   * Format the whole field, once, after dictation stops.
   *
   * Only this point in the pipeline sees a complete thought: each clip is
   * cleaned in isolation, so a list spanning a 30-second boundary cannot be
   * numbered coherently there however the prompt is written.
   *
   * Skipped when the designer dictated their own structure — they have said
   * what they want, and rewriting it would be the app overruling them.
   * `tidyDictation` returns its input on every failure, so the `===` below is
   * also the offline case: nothing changes, nothing is offered to undo.
   */
  const runTidy = async (): Promise<void> => {
    const before = textarea.value;
    if (spokeStructure || !before.trim()) return;
    const after = await tidyDictation(before, tidyKind());
    // Closed mid-request. A detached textarea keeps its value, so the equality
    // guard below would happily "format" a field nobody can see — writing to
    // it is harmless but it would also light the Undo button on a dead node.
    if (!textarea.isConnected) return;
    // The designer may have kept typing while it was in flight. Their text
    // wins over a formatting of what the field used to say.
    if (textarea.value !== before || after === before) return;
    beforeTidy = before;
    textarea.value = after;
    undoBtn.hidden = false;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * Hide the meter. The audio context is kept unless `closeContext` — it can
   * only be created inside the tap (Safari), so switching engines mid-session
   * must not throw it away or the bars never come back.
   */
  const stopMeter = (closeContext = false): void => {
    cancelAnimationFrame(rafId);
    rafId = 0;
    meter.hidden = true;
    if (closeContext) {
      void audioCtx?.close().catch(() => {});
      audioCtx = null;
    }
  };

  /**
   * Show the recording indicator. With a stream, the bars follow the
   * microphone; with null — while the BROWSER's dictation is running — only
   * the timer, because the phone has handed the microphone to its own engine
   * and opening a second stream alongside it risks breaking the dictation
   * that works offline (iPhone, 2026-09-19). The words appearing as you speak
   * are that engine's own feedback.
   */
  const startMeter = (s: MediaStream | null): void => {
    recordingStartedAt = Date.now();
    lastHeardAt = recordingStartedAt;
    silenceWarned = false;
    meter.hidden = false;
    timeEl.textContent = '0:00';

    // Bars need Web Audio. The context was created during the tap itself (see
    // the click handler): Safari starts audio processing only inside the
    // gesture, and by now we're past several awaits. Without it the timer
    // still runs and the bars hide.
    let analyser: AnalyserNode | null = null;
    const ctx = s ? audioCtx : null;
    if (ctx && s) {
      try {
        void ctx.resume().catch(() => {});
        analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(s).connect(analyser);
      } catch {
        analyser = null;
      }
    }
    meter.classList.toggle('no-level', !analyser);
    const samples = new Uint8Array(analyser?.fftSize ?? 0);

    // Bounded: runs only while the meter is shown; stopMeter() cancels it.
    const tick = (): void => {
      if (meter.hidden) return;
      const now = Date.now();
      timeEl.textContent = formatElapsed(now - recordingStartedAt);
      // A suspended context reads as perfect silence. Never call that "we
      // can't hear you" — it's our meter that can't listen, not the mic.
      if (analyser && ctx && ctx.state === 'running') {
        analyser.getByteTimeDomainData(samples);
        const lit = barsLit(levelFromSamples(samples));
        barEls.forEach((el, i) => el.classList.toggle('on', i < lit));
        if (lit > 0) {
          lastHeardAt = now;
          if (silenceWarned) { silenceWarned = false; setHint(listeningHint(tidyKind())); }
        } else if (!silenceWarned && isSilentTooLong(lastHeardAt, now)) {
          silenceWarned = true;
          setHint('We can’t hear anything — check that the right microphone is on and unmuted.', 'warn');
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  };

  // ── Stopping ──

  /**
   * End the session — the person pressed stop, or something ended it. Order
   * matters: flags first, so the recorder's final `stop` event (which fires
   * asynchronously, after this returns) uploads its clip but starts no new
   * one and never switches engines.
   */
  const stop = (): void => {
    userStopped = true;
    serverActive = false;
    if (clipTimer) { clearTimeout(clipTimer); clipTimer = null; }
    try { recorder?.stop(); } catch { /* already stopped */ }
    recorder = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    stopMeter(true);
    local?.stop();
    local = null;
    setRecording(false);
    // After the engines are down, not before: the last clip may still be in
    // flight, and formatting a field that is about to gain another sentence
    // would format the wrong text. `inflight` is re-checked inside.
    void whenQuiet(() => { void runTidy(); });
  };

  /**
   * Run `fn` once nothing is still becoming text.
   *
   * Its timer lives in its OWN set, not `retryTimers`. markPending() reads
   * `retryTimers.size`, and the modal reads the `data-voice-pending` it sets
   * to ask before a close or save would lose dictation — so parking a
   * formatting wait there would have told the designer their words were still
   * in flight for five seconds after they stopped talking, every time. Caught
   * by tracing, not by a test: this file is coverage-exempt (CLAUDE.md).
   *
   * Bounded (every retry declares what stops it) — a clip wedged in a retry
   * must not leave a timer running for the life of the page. Giving up means
   * no tidy pass, which is exactly what being offline already does.
   */
  const whenQuiet = (fn: () => void, attemptsLeft = QUIET_ATTEMPTS): void => {
    if (inflight === 0 && retryTimers.size === 0) { fn(); return; }
    if (attemptsLeft <= 0) return;
    const t = setTimeout(() => {
      quietTimers.delete(t);
      whenQuiet(fn, attemptsLeft - 1);
    }, QUIET_POLL_MS);
    quietTimers.add(t);
  };

  /**
   * Hand the rest of this session to the device's own dictation.
   *
   * The uploading recorder is torn down first: continuing to send clips to a
   * server we know is failing would waste requests that cannot succeed. The
   * clip it was recording still arrives (its `stop` event) and is uploaded —
   * if that fails too it is KEPT for Retry, not lost.
   */
  const switchToLocal = (message?: string): void => {
    if (local || userStopped) return;
    // serverActive must be cleared BEFORE stopping the recorder: its `stop`
    // event fires asynchronously and would otherwise start the next clip.
    serverActive = false;
    if (clipTimer) { clearTimeout(clipTimer); clipTimer = null; }
    try { recorder?.stop(); } catch { /* already stopped */ }
    recorder = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    stopMeter();

    // startLocalDictation calls onError SYNCHRONOUSLY when the engine refuses
    // to start — before it returns. Assigning its result straight to `local`
    // meant the handler's stop() ran first and was then undone by the
    // setRecording(true) below: a dead recognizer behind a flashing mic light,
    // which is exactly what an iPhone showed on 2026-09-17. Track the failure
    // explicitly instead of trusting the return value alone.
    let failedToStart = false;
    let failureReason = 'start-failed';
    let started = false;
    const handle = startLocalDictation({
      onText: insertText,
      onError: (reason) => {
        // Fires SYNCHRONOUSLY here when the engine refuses to start, and
        // later in the session when it gives up. Before `started`, the
        // failure is handled below — handling it here would run against a
        // `local` that hasn't been assigned yet.
        failureReason = reason;
        if (!started) { failedToStart = true; return; }
        handleLocalFailure(reason);
      },
    });

    if (!handle || failedToStart) { handleLocalFailure(failureReason); return; }
    local = handle;
    started = true;
    setRecording(true);
    // Timer only: see startMeter.
    startMeter(null);
    if (message) setHint(message, 'warn');
  };

  // ── Clips ──

  /** Upload one clip, insert its text, and decide what a failure means. */
  const handleClip = async (clip: Clip): Promise<void> => {
    if (clip.blob.size === 0) return;
    if (!local && navigator.onLine !== false) setHint('Transcribing…');
    inflight++;
    markPending();
    try {
      const { text, quota } = await transcribeClip(clip.blob, clip.seconds);
      if (quota) cachedQuota = quota;
      if (text.trim()) insertText(text);
      const fb = feedbackForSuccess(cachedQuota);
      if (kept.length > 0) showKept();
      else if (!local) setHint(serverActive ? listeningHint(tidyKind()) : fb.message, fb.tone);
      if (fb.stop) stop();
    } catch (err) {
      clip.attempts++;
      const fb = feedbackForError(err, webSpeechAvailable());
      if (fb.quota !== undefined) cachedQuota = fb.quota;
      const plan = afterClipFailure({
        feedback: fb,
        retryable: err instanceof TranscriptionFailedError && err.retryable,
        userStopped,
        attempts: clip.attempts,
      });
      if (plan.switchToLocal) switchToLocal(fb.message ?? undefined);
      else setHint(fb.message, fb.tone);
      if (plan.retry) {
        // Bounded by MAX_AUTO_RETRIES via clip.attempts (session.ts).
        const t = setTimeout(() => { retryTimers.delete(t); void handleClip(clip); }, RETRY_DELAY_MS);
        retryTimers.add(t);
        markPending();
      } else if (plan.keep) {
        kept.push(clip);
        showKept();
        markPending();
      }
      if (plan.stop) stop();
    } finally {
      inflight--;
      markPending();
    }
  };

  /** Record the next clip on the open stream. */
  const startClip = (): void => {
    if (!stream || !serverActive) return;
    const r = new MediaRecorder(stream);
    const parts: Blob[] = [];
    const startedAt = Date.now();
    r.addEventListener('dataavailable', (e) => { if (e.data && e.data.size > 0) parts.push(e.data); });
    r.addEventListener('stop', () => {
      const blob = new Blob(parts, { type: r.mimeType || 'audio/webm' });
      const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      void handleClip({ blob, seconds, attempts: 0 });
      // The next clip, unless the session ended or switched engines — both
      // clear serverActive BEFORE stopping this recorder.
      if (serverActive) { clipIndex++; startClip(); }
    });
    recorder = r;
    r.start();
    clipTimer = setTimeout(() => { try { r.stop(); } catch { /* already stopped */ } }, clipLengthMs(clipIndex));
  };

  // ── Starting ──

  const start = async (): Promise<void> => {
    if (!quotaLoaded) await primeVoiceQuota().catch(() => null);
    if (!canDictate(cachedQuota)) {
      stopMeter(true);
      setHint(quotaMessage(cachedQuota), 'warn');
      return;
    }

    starting = true;
    userStopped = false;
    // A new dictation is a new list. Numbering continues from whatever is
    // already in the field, so dictating under a list typed earlier picks up
    // where it left off rather than starting again at 1.
    formatState = continueNumbering(textarea.value);
    spokeStructure = false;
    beforeTidy = null;
    undoBtn.hidden = true;
    setHint('Getting ready…');
    // The microphone and the service check run side by side: the check
    // usually answers before the permission prompt is even dismissed.
    const online = navigator.onLine !== false;
    const [mic, service] = await Promise.all([
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((s) => ({ stream: s }), () => ({ stream: null })),
      online ? checkTranscribeService(PREFLIGHT_TIMEOUT_MS) : Promise.resolve('down' as const),
    ]);
    starting = false;

    // Tapped again while getting ready: that was "never mind".
    if (userStopped) { mic.stream?.getTracks().forEach((t) => t.stop()); stopMeter(true); setHint(null); return; }

    const plan = startPlan({ online, service, localAvailable: webSpeechAvailable() });
    if (plan.engine === 'none') {
      mic.stream?.getTracks().forEach((t) => t.stop());
      stopMeter(true);
      setHint(plan.message, plan.tone);
      return;
    }
    onLocalFailure = plan.onLocalFailure;
    if (plan.engine === 'local') {
      // The device's engine opens the microphone itself.
      mic.stream?.getTracks().forEach((t) => t.stop());
      switchToLocal(plan.message ?? undefined);
      return;
    }
    // 'server' and 'queue' both record here; offline, clips fail to send,
    // are kept, and go up when the connection returns (see 'online' below).
    if (!mic.stream) {
      stopMeter(true);
      // Denied, dismissed, or no device. All read the same to the user.
      setHint('Microphone permission is needed to dictate.', 'warn');
      return;
    }

    beginRecording(mic.stream, plan.message, plan.tone);
  };

  /** Start recording clips on `s` — the 'server' and 'queue' engines alike. */
  const beginRecording = (s: MediaStream, message: string | null, tone: 'info' | 'warn'): void => {
    stream = s;
    userStopped = false;
    serverActive = true;
    clipIndex = 0;
    startClip();
    startMeter(s);
    setRecording(true);
    setHint(message, tone);
  };

  /**
   * The browser's dictation couldn't run. Offline that's expected in Chrome
   * (its engine is Google's) and not in Safari (whose engine is on the
   * device), so fall back to recording rather than assuming either.
   */
  const handleLocalFailure = (reason: string): void => {
    local?.stop();
    local = null;
    const plan = afterLocalFailure({ reason, onFailure: onLocalFailure });
    if (plan.engine === 'none') {
      setHint(plan.message, plan.tone);
      stop();
      return;
    }
    void (async () => {
      // The permission was granted moments ago, so this doesn't prompt again.
      const s = stream ?? await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
      if (!s) { setHint('Microphone permission is needed to dictate.', 'warn'); stop(); return; }
      if (userStopped) { s.getTracks().forEach((t) => t.stop()); return; }
      beginRecording(s, plan.message, plan.tone);
    })();
  };

  btn.addEventListener('click', () => {
    if (starting) { userStopped = true; return; }
    if (recorder || local || serverActive) { stop(); return; }
    // Created HERE, synchronously inside the tap: Safari starts audio
    // processing only within a user gesture. Every path out of start() that
    // doesn't record closes it again (stopMeter).
    try { audioCtx = new AudioContext(); } catch { audioCtx = null; }
    void start();
  });

  /**
   * Send every kept clip again. A resend gets no further automatic retry: a
   * failure goes straight back to the kept list and the Retry prompt.
   */
  const resendKept = (): void => {
    const clips = kept.splice(0);
    retryBtn.hidden = true;
    markPending();
    for (const c of clips) {
      c.attempts = MAX_AUTO_RETRIES;
      void handleClip(c);
    }
  };

  retryBtn.addEventListener('click', resendKept);
  undoBtn.addEventListener('click', undoTidy);

  // Back online: send what was recorded without a connection. Bounded: one
  // resend per reconnection, and each clip gets no automatic retry of its own.
  const onOnline = (): void => { if (kept.length > 0) resendKept(); };
  window.addEventListener('online', onOnline);

  // Leaving the page mid-recording would keep the mic light on.
  window.addEventListener('pagehide', stop, { once: true });

  // Returned so the owner can end dictation when the field goes away without
  // the page going with it — closing a modal mid-sentence. Before this, the
  // overlay was removed and the recorder kept running with no button left to
  // stop it: a live microphone behind a closed dialog.
  return () => {
    // stop() schedules the tidy wait, so the timers it adds are cleared AFTER
    // it — clearing first would leave the one it just created running against
    // a textarea that is about to be removed. (The second half of the same
    // trace that found the markPending leak; this file has no tests to catch
    // either, so the order is deliberate rather than incidental.)
    stop();
    for (const t of retryTimers) clearTimeout(t);
    retryTimers.clear();
    for (const t of quietTimers) clearTimeout(t);
    quietTimers.clear();
    window.removeEventListener('online', onOnline);
  };
}

/**
 * Attach to every textarea in a container that opted in via data-voice.
 * Returns one function that ends dictation on all of them.
 */
export function attachVoiceInputs(root: ParentNode): () => void {
  const stops = [...root.querySelectorAll<HTMLTextAreaElement>('textarea[data-voice="1"]')]
    .map((ta) => attachVoiceInput(ta));
  return () => { for (const s of stops) s(); };
}

function micIcon(): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3"/>
    <path d="M5 11a7 7 0 0014 0M12 18v3"/>
  </svg>`;
}
